import React, { useState, useRef, useEffect } from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import { generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg"
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import { awardToken } from "../../awardtoken.js";

// Add a "topic" key to each quiz-enabled chapter for targeted questions.
export const Grade12BiologyLessonPages = [
  {
    title: "Welcome to Grade 12 Biology",
    content: (
      <>
        <p>
          Get ready for the most advanced Biology yet! In Grade 12, you’ll explore the cutting-edge of life science, from genetics to ecology to modern biotechnology.
        </p>
        <ul>
          <li>Molecular Genetics & Gene Technology</li>
          <li>Homeostasis & the Human Body</li>
          <li>Evolutionary Theory & Evidence</li>
          <li>Population Dynamics & Ecology</li>
          <li>Biotechnology & Society</li>
        </ul>
      </>
    ),
  },
  {
    title: "Chapter 1: Molecular Genetics & Gene Technology",
    content: (
      <>
        <h4>What is Molecular Genetics?</h4>
        <ul>
          <li>DNA structure: The double helix & nucleotides</li>
          <li>Gene expression: Transcription & translation</li>
          <li>Mutations and genetic diseases</li>
        </ul>
        <h4>Gene Technology in Action</h4>
        <ul>
          <li>Genetic engineering & GMOs</li>
          <li>CRISPR and gene editing</li>
          <li>DNA fingerprinting & forensics</li>
        </ul>
        <p><b>Did you know?</b> Gene editing can potentially cure some genetic diseases, but it also raises ethical questions.</p>
      </>
    ),
    topic: "Molecular Genetics",
  },
  {
    title: "Chapter 2: Homeostasis & Human Body Systems",
    content: (
      <>
        <h4>Staying in Balance: Homeostasis</h4>
        <ul>
          <li>Negative and positive feedback loops</li>
          <li>Hormonal regulation (endocrine system)</li>
          <li>Thermoregulation, osmoregulation, and blood sugar control</li>
        </ul>
        <h4>Major Human Body Systems</h4>
        <ul>
          <li>Nervous and endocrine systems: Communication and control</li>
          <li>Excretory and respiratory systems: Removing waste and gas exchange</li>
        </ul>
        <p><b>Try this:</b> Monitor your heart rate before and after exercise—how does your body maintain homeostasis?</p>
      </>
    ),
    topic: "Homeostasis & Physiology",
  },
  {
    title: "Chapter 3: Evolutionary Theory & Evidence",
    content: (
      <>
        <h4>The Story of Life on Earth</h4>
        <ul>
          <li>Natural selection and genetic drift</li>
          <li>Speciation and adaptation</li>
          <li>Molecular evidence for evolution (DNA & proteins)</li>
        </ul>
        <h4>Modern Evolutionary Studies</h4>
        <ul>
          <li>Phylogenetic trees and cladistics</li>
          <li>Evolution of antibiotic resistance</li>
        </ul>
        <p><b>Debate:</b> How should humans respond to the rise of antibiotic-resistant bacteria?</p>
      </>
    ),
    topic: "Evolution",
  },
  {
    title: "Chapter 4: Population Dynamics & Ecology",
    content: (
      <>
        <h4>Populations and Ecosystems</h4>
        <ul>
          <li>Population growth: Exponential vs. logistic</li>
          <li>Carrying capacity and limiting factors</li>
          <li>Community interactions: Predation, competition, symbiosis</li>
        </ul>
        <h4>Human Impact</h4>
        <ul>
          <li>Habitat loss, invasive species, and climate change</li>
          <li>Conservation biology and sustainability</li>
        </ul>
        <p><b>Action for the Future:</b> What role will you play in protecting biodiversity?</p>
      </>
    ),
    topic: "Ecology & Sustainability",
  },
  {
    title: "Chapter 5: Biotechnology & Society",
    content: (
      <>
        <h4>Biotech Innovations</h4>
        <ul>
          <li>Cloning and stem cells</li>
          <li>Personalized medicine (pharmacogenomics)</li>
          <li>Bioremediation and environmental biotechnology</li>
        </ul>
        <h4>Ethical and Societal Issues</h4>
        <ul>
          <li>Genetic privacy and discrimination</li>
          <li>Designer babies: Where do we draw the line?</li>
          <li>Who owns genetic information?</li>
        </ul>
        <p><b>Discuss:</b> Should companies be able to patent genes or life forms?</p>
      </>
    ),
    topic: "Biotechnology",
  },
  {
    title: "Final Review & Future Frontiers",
    content: (
      <>
        <p>
          Biology is moving faster than ever, with breakthroughs in genetics, medicine, and conservation. Stay curious, and keep learning—your discoveries could shape the future of life science!
        </p>
        <ul>
          <li>Review your notes and quizzes</li>
          <li>Explore recent biology news (CRISPR, climate change, COVID-19, etc.)</li>
          <li>Imagine yourself as a future scientist or biotech innovator!</li>
        </ul>
      </>
    ),
    isFinalReview: true,
  },
];

export default function Grade12BiologyLesson({ onAwardTokens }) {
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(null); // "chapter" or "final"
    const [quizResult, setQuizResult] = useState(null);
    const [user, setUser] = useState(null);
    const hasAwardedToken = useRef(false);
  
    // Track which chapters have been passed
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("bio12ChaptersPassed") || "{}")
    );
  
    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);
  
    // Prepare quiz questions: for chapter OR final
    const currentTopic = Grade12BiologyLessonPages[current]?.topic;
    const isFinalReview = Grade12BiologyLessonPages[current]?.isFinalReview;
    let quizQuestions = [];
    if (showQuiz === "chapter" && currentTopic) {
      quizQuestions = generateAdvancedScienceQuiz({
        subject: "Biology",
        grade: 12,
        topic: currentTopic,
        count: 5,
      });
    } else if (showQuiz === "final") {
      const topics = [
        "Molecular Genetics",
        "Homeostasis & Physiology",
        "Evolution",
        "Ecology & Sustainability",
        "Biotechnology"
      ];
      let pool = [];
      topics.forEach(topic => {
        pool = pool.concat(generateAdvancedScienceQuiz({
          subject: "Biology",
          grade: 12,
          topic,
          count: 10,
        }));
      });
      quizQuestions = pool.sort(() => Math.random() - 0.5).slice(0, 15);
    }
  
    useEffect(() => {
      if (quizResult && !hasAwardedToken.current) {
        const percent = (quizResult.score / quizResult.total) * 100;
        if (percent >= 80) {
          if (typeof onAwardTokens === "function") {
            onAwardTokens(1);
          } else {
            awardToken();
          }
          hasAwardedToken.current = true;
        }
      }
    }, [quizResult, onAwardTokens]);
  
    // Navigation: gated like biology 11/chemistry/physics
    const handleNextPage = () => {
        const nextIndex = current + 1;
        const nextPage = Grade12BiologyLessonPages[nextIndex];
      
        // Only gate if currentTopic is set (i.e., not intro page)
        if (
          nextPage &&
          currentTopic &&
          nextPage.topic !== currentTopic &&
          currentTopic !== "Course Introduction" &&
          !chaptersPassed[currentTopic]
        ) {
          setShowQuiz("chapter");
          setQuizResult(null);
          return;
        }
      
        setCurrent(nextIndex);
      };
      
  
    // Quiz UI handler (update chaptersPassed if passed)
    if (showQuiz && quizQuestions.length > 0) {
      return (
        <ScienceStoryModeQuiz
  questions={quizQuestions}
  onFinish={(score, total) => {
    setShowQuiz(null);
    setQuizResult({ score, total });
    hasAwardedToken.current = false;
    const percent = total > 0 ? Math.round((score / total) * 100) : 0;

    // --- Save quiz result for Student Dashboard ---
    if (showQuiz === "chapter" && currentTopic) {
      const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
      quizResults[`biology_g12_${currentTopic}`] = {
        score,
        total,
        percent,
        lastCompleted: Date.now(),
      };
      localStorage.setItem("quizResults", JSON.stringify(quizResults));
    }
    if (showQuiz === "final") {
      const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
      quizResults["biology_g12_final"] = {
        score,
        total,
        percent,
        lastCompleted: Date.now(),
      };
      localStorage.setItem("quizResults", JSON.stringify(quizResults));
    }
    // ---------------------------------------------

    if (percent >= 80 && currentTopic && showQuiz === "chapter") {
      setChaptersPassed(prev => {
        const updated = { ...prev, [currentTopic]: true };
        localStorage.setItem("bio12ChaptersPassed", JSON.stringify(updated));
        return updated;
      });
    }
  }}
  studentAvatar={user?.avatar ?? defaultProfile}
/>
      );
    }
  
    // Results/congrats screen
    if (quizResult) {
      const percent = Math.round((quizResult.score / quizResult.total) * 100);
      const passed = percent >= 80;
      return (
        <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
          <h2 style={{ color: "#d4145a" }}>Quiz Complete!</h2>
          <p style={{ fontSize: 22, margin: "18px 0" }}>
            Your Score: {quizResult.score} / {quizResult.total} ({percent}%)
          </p>
          {passed ? (
            <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
              🎉 Congratulations! You earned a Science token!
            </div>
          ) : (
            <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
              Try again to earn a Science token (80% required).
            </div>
          )}
          {passed ? (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => {
                setQuizResult(null);
                setCurrent(c => c + 1);
              }}
            >
              Next Chapter
            </button>
          ) : (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => setQuizResult(null)}
            >
              Return to Lesson
            </button>
          )}
        </div>
      );
    }
  
    // Main lesson rendering
    return (
      <div className="lesson-page">
        <ProgressBar progress={Math.round((current / (Grade12BiologyLessonPages.length - 1)) * 100)} />
        <LessonUI
  pages={[Grade12BiologyLessonPages[current]]}
  currentPageIndex={0}
  onPrevPage={current > 0 ? () => setCurrent(c => c - 1) : undefined}
  onNextPage={current < Grade12BiologyLessonPages.length - 1 ? handleNextPage : undefined}
  backTo="/science"
/>

        {/* Chapter quiz (for slides with a topic) */}
        {currentTopic && !isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("chapter");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Start Quiz for This Chapter
            </button>
          </div>
        )}
        {/* Final Quiz button (only on last slide) */}
        {isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("final");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Take Final Quiz for Grade 12 Biology
            </button>
          </div>
        )}
      </div>
    );
  }
  
